import { Spinner } from '@crm/ui';
import { CalendarClock, Check, ClipboardCheck, Scissors, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toErrorMessage } from '@/modules/auth/utils';
import { StepCustomer } from './step-customer';
import { StepDone } from './step-done';
import { StepProfessional } from './step-professional';
import { StepService } from './step-service';
import { StepSummary } from './step-summary';
import {
  WIZARD_LABELS,
  WIZARD_STEPS,
  type AppointmentWizard as Wizard,
  type WizardStep,
} from './use-appointment-wizard';

const ICONS: Record<WizardStep, React.ComponentType<{ className?: string }>> = {
  customer: UserRound,
  service: Scissors,
  professional: CalendarClock,
  summary: ClipboardCheck,
};

/** Passo curto para o indicador; o titulo completo fica no cabecalho. */
const SHORT: Record<WizardStep, string> = {
  customer: 'Cliente',
  service: 'Servico',
  professional: 'Horario',
  summary: 'Resumo',
};

/** Um passo so e alcancavel quando ja tem o que ele precisa. */
function canReach(wizard: Wizard, step: WizardStep): boolean {
  if (!wizard.editable) return false;

  switch (step) {
    case 'customer':
      return true;
    case 'service':
      return wizard.customer !== null;
    case 'professional':
      // Com a comanda montada, o passo continua alcancavel: clicar nele e
      // remarcar o primeiro servico, que e o que se quer ao voltar ali.
      return wizard.current !== null || wizard.rows.length > 0;
    case 'summary':
      return wizard.rows.length > 0;
    default:
      return false;
  }
}

export function AppointmentWizard({ wizard }: { wizard: Wizard }) {
  if (wizard.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (wizard.loadError) {
    return <p className="text-base text-destructive">{toErrorMessage(wizard.loadError)}</p>;
  }

  // Agendou pela pagina publica: o wizard da lugar a confirmacao.
  if (wizard.isConfirmed) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <StepDone wizard={wizard} />
      </div>
    );
  }

  const currentIndex = WIZARD_STEPS.indexOf(wizard.step);
  const steps = wizard.editable ? WIZARD_STEPS : (['summary'] as WizardStep[]);

  return (
    // Mais largo no monitor: cabe a comanda inteira em uma linha por servico
    // e sobra menos rolagem.
    <div className="mx-auto w-full max-w-3xl space-y-6 xl:max-w-5xl">
      {steps.length > 1 ? (
        <nav aria-label="Etapas do agendamento">
          {/* A linha liga um passo ao outro e mostra ate onde ja se chegou:
              o trecho ja percorrido fica na cor primaria. */}
          <ol className="flex items-start">
            {steps.map((step, index) => {
              const Icon = ICONS[step];
              const done = index < currentIndex;
              const active = step === wizard.step;
              const reachable = canReach(wizard, step);

              return (
                <li key={step} className="relative flex flex-1 flex-col items-center">
                  {index > 0 ? (
                    <span
                      aria-hidden
                      className={cn(
                        // z-0 com o passo em z-10: a linha passa por tras dos icones.
                        'absolute right-1/2 top-5 z-0 h-0.5 w-full',
                        index <= currentIndex ? 'bg-primary' : 'bg-border',
                      )}
                    />
                  ) : null}

                  <button
                    type="button"
                    disabled={!reachable}
                    aria-current={active ? 'step' : undefined}
                    onClick={() => {
                      const primeira = wizard.rows[0];
                      // Sem servico em edicao, o passo do horario nao sabe do
                      // que esta falando: entra pelo primeiro da comanda.
                      if (step === 'professional' && !wizard.current && primeira) {
                        wizard.editRow(primeira);
                        return;
                      }

                      wizard.goTo(step);
                    }}
                    className="relative z-10 flex flex-col items-center gap-1.5"
                  >
                    <span
                      className={cn(
                        // Fundo sempre opaco: com transparencia a linha
                        // aparece por dentro do circulo e parece cortada.
                        'flex h-10 w-10 items-center justify-center rounded-full border-2 bg-background transition-colors',
                        active && 'border-primary bg-primary text-primary-foreground',
                        done && !active && 'border-primary text-primary',
                        !active && !done && 'border-border text-muted-foreground',
                        // Passo ainda inalcancavel esmaece na cor, nunca em
                        // opacidade: transparente deixaria a linha aparecer.
                        !reachable && !active && 'border-border/60 text-muted-foreground/50',
                      )}
                    >
                      {done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                    </span>
                    <span
                      className={cn(
                        'text-xs sm:text-sm',
                        active ? 'font-semibold text-foreground' : 'text-muted-foreground',
                        !reachable && !active && 'text-muted-foreground/60',
                      )}
                    >
                      {SHORT[step]}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}

      <header className="space-y-1">
        <h2 className="text-xl font-semibold sm:text-2xl">{WIZARD_LABELS[wizard.step]}</h2>
      </header>

      {wizard.error ? (
        <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-base text-destructive">
          {wizard.error}
        </p>
      ) : null}

      {wizard.mutationError ? (
        <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-base text-destructive">
          {toErrorMessage(wizard.mutationError)}
        </p>
      ) : null}

      {wizard.step === 'customer' ? <StepCustomer wizard={wizard} /> : null}
      {wizard.step === 'service' ? <StepService wizard={wizard} /> : null}
      {wizard.step === 'professional' ? <StepProfessional wizard={wizard} /> : null}
      {wizard.step === 'summary' ? <StepSummary wizard={wizard} /> : null}
    </div>
  );
}
